import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * TurnControls Component
 * Displays action buttons for the current turn phase
 *
 * @param {string} currentPlayer - Name of active player
 * @param {string} playerName - Current user's name
 * @param {string} turnPhase - Current turn phase (initial, whiteSum, colorDice, complete)
 * @param {boolean} gameInitialized - Whether game has been initialized
 * @param {object} turnActions - Turn actions state {whiteSumMarked, whiteSumSkipped, colorDiceMarked}
 * @param {object} playerWhitePhaseComplete - Object mapping player names to white phase completion
 * @param {boolean} autoEndingTurn - Whether turn is auto-ending
 * @param {function} onRollDice - Callback for rolling dice
 * @param {function} onSkipWhiteDice - Callback for skipping white dice (active player)
 * @param {function} onNonActiveSkipWhite - Callback for skipping white dice (non-active player)
 * @param {function} onTakePenalty - Callback for taking penalty
 * @param {function} onEndTurn - Callback for ending turn
 */
const TurnControls = ({
  currentPlayer,
  playerName,
  turnPhase,
  gameInitialized,
  turnActions,
  playerWhitePhaseComplete,
  autoEndingTurn,
  onRollDice,
  onSkipWhiteDice,
  onNonActiveSkipWhite,
  onTakePenalty,
  onEndTurn
}) => {
  const { t } = useLanguage();

  // Determine if we should show color phase buttons
  const shouldShowColorPhaseButtons = () => {
    return currentPlayer === playerName && turnPhase === 'colorDice' && !autoEndingTurn;
  };

  // Determine if penalty button should be shown
  const shouldShowPenaltyButton = () => {
    const shouldShow = shouldShowColorPhaseButtons() &&
                       !turnActions.colorDiceMarked &&
                       (turnActions.whiteSumSkipped === true ||
                        (playerWhitePhaseComplete[playerName] === true && !turnActions.whiteSumMarked));
    return shouldShow;
  };

  // Determine if end turn button should be shown
  const shouldShowEndTurnButton = () => {
    const penaltyShowing = shouldShowPenaltyButton();

    const shouldShow = shouldShowColorPhaseButtons() &&
                      !penaltyShowing &&
                      (turnActions.whiteSumMarked === true ||
                       turnActions.colorDiceMarked === true ||
                       (turnPhase === 'complete'));

    return shouldShow;
  };

  const buttonStyle = (backgroundColor) => ({
    backgroundColor,
    color: 'white',
    padding: '0.75rem 1.25rem',
    borderRadius: '0.5rem',
    fontWeight: '600',
    textAlign: 'center',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    whiteSpace: 'normal',
    fontSize: '0.95rem',
    wordWrap: 'break-word',
    hyphens: 'auto',
    lineHeight: '1.3'
  });

  return (
    <div className="game-actions">
      {/* Roll Dice Button - Initial phase, active player only */}
      {currentPlayer === playerName &&
       turnPhase === 'initial' &&
       gameInitialized && (
        <button className="roll-button" onClick={onRollDice}>
          {t('buttons.rollDice')}
        </button>
      )}

      {/* Skip White Dice Button - White sum phase, all players */}
      {turnPhase === 'whiteSum' &&
       !playerWhitePhaseComplete[playerName] &&
       !turnActions.whiteSumMarked &&
       !turnActions.whiteSumSkipped && (
        <button
          className="skip-button"
          onClick={currentPlayer === playerName ? onSkipWhiteDice : onNonActiveSkipWhite}
          style={buttonStyle('#6366f1')}
        >
          {t('buttons.skipWhiteDice')}
        </button>
      )}

      {/* Take Penalty Button - Color phase when white dice skipped */}
      {shouldShowPenaltyButton() && (
        <button
          className="penalty-button"
          onClick={onTakePenalty}
          style={buttonStyle('#f97316')}
        >
          {t('buttons.takePenalty')}
        </button>
      )}

      {/* End Turn Button - Color phase when actions completed */}
      {shouldShowEndTurnButton() && (
        <button
          className="end-turn-button"
          onClick={onEndTurn}
          style={buttonStyle('#10b981')}
        >
          {t('buttons.skipColorDice')}
        </button>
      )}
    </div>
  );
};

export default TurnControls;
